import { useCallback, useEffect, useRef, useState } from 'react';
import { MsalAuthErrorToastId, OtherUserAuthenticatedError, SignInTimeout } from 'AuthConstants';
import { authlog } from 'AuthLogger';
import { authService } from 'AuthService';
import { pageDataService } from 'common';
import { SnackbarType } from 'common/components/Notifications';
import { isSafari } from 'common/utilities/BrowserHelpers';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { useLocalizedStrings } from 'i18n';
import { AuthResult } from 'nativeAppShim';
import { ILoginRequestState, setAuthResult, setUseDefaultScope, useAppDispatch, useAppSelector } from 'store';
import { useNotifications } from 'store/layout/hooks/useNotifications';
import { useWebView } from 'store/webView/useWebView';

import { CacheLookupPolicy, InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

type RefreshTokenState = 'Unknown' | 'Valid' | 'Expired';

export const useMsalAuth = (email: string) => {
  const { instance, inProgress } = useMsal();

  const isAuthenticated = useIsAuthenticated({ username: email });
  const { isWebView, supportsLocalAuth, authenticateUser } = useWebView();
  const [RefreshTokenState, setRefreshTokenState] = useState<RefreshTokenState>('Unknown');
  const isMfaAuthenticated = isAuthenticated && RefreshTokenState === 'Valid';
  const dispatch = useAppDispatch();
  const { showToast, dismissNotification } = useNotifications();
  const { t } = useLocalizedStrings();

  // login redirect states
  const isLoginAttempted = useRef(false);
  const { loginRedirectSuccess, useDefaultScope } = useAppSelector((state) => state.auth);
  const loginCount = useAppSelector((state) => state.auth.requestState.loginCount);

  const getMfaTokenSilent = useCallback(
    async (calledOnLoginRedirectSuccess = false) => {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) {
        authlog('getMfaTokenSilent => no active account');
        return undefined;
      }

      const mfaScope = pageDataService.getMfaScope();

      authlog('getMfaTokenSilent start');
      TelemetryProvider.Instance.trackEvent('getMfaTokenSilent', { mfaScope, calledOnLoginRedirectSuccess });
      let result;
      try {
        result = await instance.acquireTokenSilent({
          scopes: [mfaScope],
          account: activeAccount,
          forceRefresh: false,
          cacheLookupPolicy: CacheLookupPolicy.Default,
        });
      } catch (error) {
        setRefreshTokenState('Expired');
        authlog(`getMfaTokenSilent failed => ${error}`);
        TelemetryProvider.Instance.trackEvent('getMfaTokenSilentFailed', { message: error as string, calledOnLoginRedirectSuccess });
      }

      if (result) {
        // If silent token acquisition was successful, that means refresh token is valid
        setRefreshTokenState('Valid');
        TelemetryProvider.Instance.trackEvent('getMfaTokenSilentSuccess', { calledOnLoginRedirectSuccess });
        authlog(`getMfaTokenSilent success => ${result.account?.username}, ${result.expiresOn}, ${result.scopes}`);
        return result.accessToken;
      }

      return undefined;
    },
    [instance]
  );

  const handleLoginError = useCallback(
    (error: unknown): boolean => {
      authlog('handleLoginError start');
      let errorHandled = false;
      let toastMessage: string | undefined;

      if (typeof error === 'string') {
        // Advanced tracking and fingerprinting protection in Safari InPrivate mode may cause loginRedirect to fail.
        if (localeIgnoreCaseEquals(error, SignInTimeout)) {
          const isSafariBrowser = isSafari();
          authlog(`handleLoginError => SignInTimeout - isSafari: ${isSafariBrowser}`);
          TelemetryProvider.Instance.trackEvent('mfaLoginHandleError', { ErrorType: 'SignInTimeout', IsSafari: isSafariBrowser });
          if (isSafariBrowser) {
            errorHandled = true;
            // TODO: update the string once finalized strings are provided: Deliverable #55114631
            toastMessage = t('MsalAuth.ErrorMessage.SignInTimeout');
          }
        }
        // User is authenticated with the wrong account (different account from the account currently signed into Defender).
        else if (localeIgnoreCaseEquals(error, OtherUserAuthenticatedError)) {
          authlog('handleLoginError => OtherUserAuthenticatedError');
          TelemetryProvider.Instance.trackEvent('mfaLoginHandleError', { ErrorType: 'OtherUserAuthenticatedError' });
          errorHandled = true;
          toastMessage = t('MsalAuth.ErrorMessage.OtherUserAuthenticated', { email });
        }
      } else if (typeof error === 'object') {
        const parsedError = error as { name: string; message: string };

        // Error thrown for users with their MFA setting turned ON who try to sign in with their password while their refresh token is in expired state.
        if (
          parsedError.name === 'Error' &&
          typeof parsedError.message === 'string' &&
          !!parsedError.message?.startsWith('invalid_grant: 70000')
        ) {
          authlog('handleLoginError => ServerError: invalid_grant: 70000');
          TelemetryProvider.Instance.trackEvent('mfaLoginHandleError', {
            ErrorType: 'ServerError: invalid_grant: 70000',
            PreviousUseDefaultScopeState: useDefaultScope, // used to track if user got the error again even after making the call with default scope
          });
          // Use default scope for the next login attempt to resolve the error
          dispatch(setUseDefaultScope(true));
          errorHandled = true;
          toastMessage = t('MsalAuth.ErrorMessage.GenericError');
        }
      }

      if (!errorHandled) {
        authlog(`handleLoginError => UnhandledError: ${error}`);
        TelemetryProvider.Instance.trackEvent('mfaLoginHandleError', { ErrorType: 'UnhandledError', Error: error as string });
        toastMessage = t('MsalAuth.ErrorMessage.GenericError');
      }

      if (toastMessage) {
        showToast({
          id: MsalAuthErrorToastId,
          message: toastMessage,
          messageType: SnackbarType.error,
          dismissable: true,
          dismissDelayInMs: 0, // user must manually dismiss the toast
        });
      }

      authlog(`handleLoginError success => errorHandled: ${errorHandled}`);
      return errorHandled;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useDefaultScope]
  );

  const mfaLoginRedirect = useCallback(
    async (redirectStartPagePath: string, isCustomPopup = false, loginWithDefaultScope = false) => {
      authlog(`mfaLoginRedirect triggered from ${window.location.href}. inProgress state: ${inProgress}`);

      if (isLoginAttempted.current) {
        TelemetryProvider.Instance.trackEvent('msalMultipleLoginAttemptsDetected');
        authlog(`skipping mfaLoginRedirect => disabling multiple login attempts. inProgress state: ${inProgress}`);
        return;
      }

      if (window.location.href.includes('signin-spa')) {
        authlog(`skipping mfaLoginRedirect => page reload after loginRedirect`);
        return;
      }

      if (inProgress !== InteractionStatus.None) {
        authlog(`skipping mfaLoginRedirect => inProgress state: ${inProgress}`);
        return;
      }

      if (isMfaAuthenticated) {
        // Login should not be called if the user is already authenticated.
        // This is an unexpected case that we will log, and login will proceed as normal.
        TelemetryProvider.Instance.trackEvent('mfaLoginRedirectAlreadyMfaAuthenticated', {
          isMfaAuthenticated,
          isAuthenticated,
          refreshTokenState: RefreshTokenState,
        });
        authlog(`mfaLoginRedirect => isMfaAuthenticated: true, refreshTokenState: ${RefreshTokenState}`);
      }

      isLoginAttempted.current = true;
      // Set origin url so that we can navigate back after login is successful
      const originUrl = redirectStartPagePath + window.location.search;
      const requestState: ILoginRequestState = { loginCount: loginCount + 1, originUrl, isCustomPopup };
      const loginStateString = JSON.stringify(requestState);

      // Note: we need to use loginWithDefaultScope as a parameter instead of useDefaultScope from redux, as the redux state is not correctly reflected
      // when using the loginRedirect flow on a custom popup window. This parameter will come from a query parameter passed into the custom window.
      authlog(`mfaLoginRedirect useDefaultScope => ${loginWithDefaultScope}`);
      authlog(`mfaLoginRedirect start: state => ${loginStateString}`);
      TelemetryProvider.Instance.trackEvent('mfaLoginRedirectStart', { ...requestState, loginWithDefaultScope });

      await instance
        .loginRedirect({
          // If useDefaultScope (loginWithDefaultScope) is set to true, set the scope to be empty to trigger the default login flow.
          // Note that we still check for the specific scope when getting the MFA token in getMfaTokenSilent.
          scopes: loginWithDefaultScope ? [] : [pageDataService.getMfaScope()],
          state: loginStateString,
          prompt: 'login', // force the user to enter their credentials on that request, negating single-sign on
          loginHint: pageDataService.getUserData().userPrincipalName, // login flow would pre-fill the email address with the user's email address
        })
        .catch((error) => {
          authlog(`mfaLoginRedirect failed => ${error}`);
          TelemetryProvider.Instance.trackException({
            name: 'mfaLoginRedirectFailed',
            message: error as string,
          });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance]
  );

  const loginMfa = useCallback(
    async (useMfaScope = true) => {
      // Remove all error toasts before new login session
      dismissNotification(MsalAuthErrorToastId);

      // If WebView supports local auth, use it.
      if (isWebView) {
        TelemetryProvider.Instance.trackEvent('mfaLoginStart', { IsWebView: true });
        if (supportsLocalAuth) {
          authlog('loginMfa: calling a platform authentication');
          dispatch(setAuthResult(AuthResult.None));
          authenticateUser();
          return;
        }
        // Not supported scenario
        authlog('loginMfa: local auth is not supported');
        TelemetryProvider.Instance.trackException({
          name: 'loginMfaNotSupported',
          message: 'Unexpected flow as auth check should be disabled',
        });
        return;
      }

      setRefreshTokenState('Unknown');

      // MSAL loginPopup is not working well on iOS and Android devices - popup window gets stuck.
      // Similar issue was observed rarely on Windows devices as well.
      // Use loginRedirect with a custom popup window.
      // Pass in useDefaultScope from redux as a prop to use as a query parameter in the custom popup window.
      const shouldUseDefaultScope = !useMfaScope || useDefaultScope;

      TelemetryProvider.Instance.trackEvent('mfaLoginStart', { IsWebView: false });
      authService.createCustomLoginPopup(handleLoginError, shouldUseDefaultScope);
    },
    [isWebView, supportsLocalAuth, dispatch, authenticateUser, handleLoginError, useDefaultScope]
  );

  useEffect(() => {
    if (loginRedirectSuccess) {
      authlog('useMsalAuth: loginRedirectSuccess => calling getMfaTokenSilent');
      TelemetryProvider.Instance.trackEvent('mfaLoginRedirectSuccess');
      getMfaTokenSilent(true);
    }
  }, [getMfaTokenSilent, loginRedirectSuccess]);

  return { loginMfa, mfaLoginRedirect, getMfaTokenSilent, isMfaAuthenticated };
};
